/* React hooks */
import { useEffect, useState } from "react";
/* Components */
import useAuth from "../../hooks/useAuth";
import { useLocation, useParams } from "react-router-dom";
import { getDeceasedInfos, updateDeceasedPortraitFromLib, uploadDeceasedPortrait } from "../../api/pro/deceasedReq";
import useDeceased from "../../hooks/useDeceased";
import {
    checkRequiredFields,
    fileToBase64,
    getErrorListFromValidation,
    removeErrorList,
    validateFormInput
} from "../../helper/tools";
import { safeJwtDecode } from "../../helper/jwtHelper";
import { uploadFile } from "../../api/admin/agencyReq";
import useData from "../../hooks/useData";
import { getDefaultImageLibrary } from "../../api/admin/imageReq";
import useRefreshToken from "../../hooks/useRefreshToken";
import Icon from "../../components/common/Icon";


export default function FormOtherDeceasedInfos({ setShowModal, apiAgencyProInfos, modalType = "all" }) {
    const { deceasedInfos, setDeceasedInfos, newForm, setNewForm } = useDeceased();
    const { auth } = useAuth();
    const token = auth?.token;
    const decodedToken = safeJwtDecode(token);
    const agencyId = decodedToken?.agency;
    const logoFullUrl = decodedToken?.agency_logo;
    const { id } = useParams();
    const [selectedCompressedFile, setSelectedCompressedFile] = useState();
    const [fileName, setFileName] = useState();
    const [values, setValues] = useState({
        isListed: 1,
        agencyId: agencyId,
        deceasedId: 0
    });
    const IMG_FOLDER = process.env.REACT_APP_LOCAL_ASSETS_BASE_URL;
    const { pathname } = useLocation();
    const { imageLib, setImageLib } = useData();
    const [selectedPortrait, setSelectedPortrait] = useState();
    const [selectedImage, setSelectedImage] = useState();
    const [rootImageList, setRootImageList] = useState([]);
    const [showPortraitModal, setShowPortraitModal] = useState(false);
    const refresh = useRefreshToken();

    // Bloquer le scroll du body quand la modal est ouverte
    useEffect(() => {
        if (showPortraitModal) {
            document.body.classList.add('modal-open');
        } else {
            document.body.classList.remove('modal-open');
        }
        
        // Cleanup: retirer la classe quand le composant est démonté
        return () => {
            document.body.classList.remove('modal-open');
        };
    }, [showPortraitModal]);

    useEffect(() => {
        if (pathname.includes("infos-defunt") && deceasedInfos && Object.keys(deceasedInfos).length > 0) {
            
            setValues({
                deceasedId: id,
                isListed: deceasedInfos?.isListed,
                portrait: deceasedInfos?.portrait,
                agencyId: agencyId,
            })
        } else if (pathname.includes("nouveau")) {
            if (newForm && Object.keys(newForm).length > 0 && newForm?.isListed !== undefined) {
                setValues({
                    deceasedId: newForm?.deceasedId || 0,
                    isListed: newForm.isListed,
                    portrait: newForm?.portrait,
                    agencyId: agencyId,
                })
            } else {
                setValues({
                    deceasedId: 0,
                    isListed: values?.isListed,
                    portrait: undefined,
                    agencyId: agencyId,
                })
            }
        } else if (pathname.includes("agence")) {
            setValues({
                agencyId: agencyId,
                logo: logoFullUrl
            })
            if (logoFullUrl) {
                setSelectedImage(logoFullUrl);
            }
        }
    }, [deceasedInfos, newForm, pathname, id, agencyId, logoFullUrl]);

    useEffect(() => {
        if ((pathname.includes("infos-defunt") || pathname.includes("nouveau")) &&
            deceasedInfos?.useGalleryPortrait &&
            deceasedInfos?.portrait) {
            setSelectedPortrait(deceasedInfos.portrait);

            const agencyImagePath = `${IMG_FOLDER}/img/deceased_library/${agencyId}/${deceasedInfos.portrait}`;
            const rootImagePath = `${IMG_FOLDER}/img/deceased_library/${deceasedInfos.portrait}`;

            const img = new Image();
            img.onload = () => {
                setSelectedImage(agencyImagePath);
            };
            img.onerror = () => {
                setSelectedImage(rootImagePath);
            };
            img.src = agencyImagePath;
        } else if (pathname.includes("nouveau") && newForm?.useGalleryPortrait && newForm?.portrait) {
            setSelectedPortrait(newForm.portrait);
            setSelectedImage(newForm.image || `${IMG_FOLDER}/img/deceased_library/${newForm.portrait}`);
        }
    }, [deceasedInfos, newForm, pathname, agencyId, IMG_FOLDER]);

    useEffect(() => {
        if (pathname?.includes("nouveau") && (!newForm?.hasOwnProperty('isListed') || newForm?.isListed === undefined)) {
            setNewForm({ ...newForm, isListed: values?.isListed });
        }
    }, [pathname, values?.isListed]);

    const handleRemoveModal = () => {
        setShowModal(false)
    };

    const onChange = (e) => {
        let value = e.target.value;

        const newValues = { ...values, [e.target.name]: value };
        setValues(newValues);

        if (pathname?.includes("nouveau")) {
            setNewForm({ ...newForm, ...newValues });
        }

        validateFormInput(e);
    };

    const apiDeceasedInfos = async () => {
        const response = await getDeceasedInfos(token, id)
        setDeceasedInfos(response)
    }

    const apiImageLib = async () => {
        const response = await getDefaultImageLibrary(token)
        setImageLib(response)
    }

    useEffect(() => {
        if ((pathname.includes("infos-defunt") || pathname?.includes("nouveau"))) {
            apiDeceasedInfos()
            apiImageLib()
        }
    }, []);

    useEffect(() => {
        if (imageLib && imageLib.length > 0) {
            const enabledImages = imageLib.filter(image => image?.enabled && image?.category === 1);
            const combined = [];

            // Images de l'agence affichées en priorité, avant les images communes (admin).
            enabledImages.forEach(image => {
                combined.push({
                    ...image,
                    path: `${IMG_FOLDER}/img/deceased_library/${agencyId}/${image?.url}`,
                    source: 'agency',
                    uniqueKey: `agency-${agencyId}-${image.id}`
                });
            });
            enabledImages.forEach(image => {
                combined.push({
                    ...image,
                    path: `${IMG_FOLDER}/img/deceased_library/${image?.url}`,
                    source: 'root',
                    uniqueKey: `root-${image.id}`
                });
            });

            setRootImageList(combined);
        }
    }, [imageLib, agencyId, IMG_FOLDER]);

    const apiUploadedFile = async () => {
        if ((pathname.includes("infos-defunt") || pathname?.includes("nouveau"))) {
            if (selectedPortrait) {
                let bodyLib = {
                    deceasedId: values?.deceasedId,
                    url: values?.url || selectedPortrait,
                    agencyId: agencyId,
                    isListed: values?.isListed
                };

                await updateDeceasedPortraitFromLib(token, bodyLib)
                    .then(async (response) => {
                        removeErrorList();
                        if (!response?.response?.data?.error) {
                            const bodyIsListed = {
                                file: "undefined",
                                agencyId: agencyId,
                                deceasedId: values?.deceasedId || 0,
                                isListed: values?.isListed,
                                file_type: "portrait",
                                file_name: ""
                            };

                            await uploadDeceasedPortrait(token, bodyIsListed)
                                .then(response2 => {
                                    removeErrorList();
                                    if (!response2?.response?.data?.error) {
                                        apiDeceasedInfos();
                                        setShowModal(false);
                                    } else {
                                        getErrorListFromValidation(response2?.response?.data?.error);
                                    }
                                });
                        } else {
                            getErrorListFromValidation(response?.response?.data?.error);
                        }
                    });
            }
            else if (selectedCompressedFile) {
                const body = {
                    file: selectedCompressedFile,
                    agencyId: agencyId,
                    deceasedId: values?.deceasedId || 0,
                    isListed: values?.isListed,
                    file_type: "portrait",
                    file_name: fileName,
                    useGalleryPortrait: false
                };

                await uploadDeceasedPortrait(token, body)
                    .then(response => {
                        removeErrorList();
                        if (!response?.response?.data?.error) {
                            setValues({ ...values, portrait: response?.file_name });
                            apiDeceasedInfos();
                            setShowModal(false);
                        } else {
                            getErrorListFromValidation(response?.response?.data?.error);
                        }
                    });
            }
            else {
                const body = {
                    file: "undefined",
                    agencyId: agencyId,
                    deceasedId: values?.deceasedId || 0,
                    isListed: values?.isListed,
                    file_type: "portrait",
                    file_name: ""
                };

                await uploadDeceasedPortrait(token, body)
                    .then(response => {
                        removeErrorList();
                        if (!response?.response?.data?.error) {
                            apiDeceasedInfos();
                            setShowModal(false);
                        } else {
                            getErrorListFromValidation(response?.response?.data?.error);
                        }
                    });
            }
        } else if (pathname.includes("agence")) {
            const formData = new FormData();
            formData.append('file', selectedCompressedFile);
            formData.append('agencyId', agencyId);
            formData.append('file_type', "logo");
            formData.append('file_name', fileName);
            const response = await uploadFile(token, formData, agencyId)
                .then(response => {
                    setValues({ ...values, logo: response?.file_name });
                });
            apiAgencyProInfos();
        }
    };

    const handleFileUpload = async (e) => {
        e.preventDefault();

        if (!checkRequiredFields()) {
            return;
        }

        if (pathname.includes("infos-defunt")) {
            await apiUploadedFile();
            apiDeceasedInfos();
            setShowModal(false);
        } else if (pathname.includes("agence")) {
            if (selectedCompressedFile || values?.logo) {
                await apiUploadedFile()
                setShowModal(false)
                apiAgencyProInfos()
                refresh(localStorage.getItem("refresh_token"))
            } else {
                alert(`Veuillez sélectionner une image`);
            }
        } else if (pathname?.includes("nouveau")) {
            setNewForm({
                ...newForm,
                file: selectedCompressedFile,
                isListed: values?.isListed,
                portrait: values?.portrait,
                image: selectedImage,
                url: values?.url,
                useGalleryPortrait: values?.useGalleryPortrait,
                file_name: fileName
            })
            setShowModal(false);
        }
    }

    const handleLibFileSelected = (url, imagePath = null) => {
        if (selectedPortrait === url) {
            setSelectedPortrait(null);
            setSelectedImage(null);
            const newValues = { ...values, url: "", useGalleryPortrait: false, portrait: "" };
            setValues(newValues);

            if (pathname?.includes("nouveau")) {
                setNewForm({ ...newForm, ...newValues, isListed: values?.isListed, file: null, file_name: null });
            }
        } else {
            setSelectedPortrait(url);
            const displayPath = imagePath || `${IMG_FOLDER}/img/deceased_library/${url}`;
            setSelectedImage(displayPath);
            setSelectedCompressedFile(null);
            setFileName("");
            const newValues = { ...values, url: url, useGalleryPortrait: true, portrait: url };
            setValues(newValues);

            if (pathname?.includes("nouveau")) {
                setNewForm({ ...newForm, ...newValues, isListed: values?.isListed, file: null, file_name: null });
            }
        }
    }

    const handleFileChange = async (e) => {
        let selectedFile = e.target.files[0];
        if (e && selectedFile) {
            setFileName(selectedFile.name);
            const reader = new FileReader();
            reader.onloadend = () => {
                setSelectedImage(reader.result);
            };
            reader.readAsDataURL(selectedFile);

            try {
                const base64Photo = await fileToBase64(selectedFile);
                setSelectedCompressedFile(base64Photo);
                setSelectedImage(base64Photo);
                setSelectedPortrait(null);
                const newValues = { ...values, url: "", useGalleryPortrait: false, portrait: selectedFile?.name };
                setValues(newValues);

                if (pathname?.includes("nouveau")) {
                    setNewForm({
                        ...newForm,
                        file: base64Photo,
                        image: base64Photo,
                        ...newValues,
                        isListed: values?.isListed,
                        file_name: selectedFile.name
                    });
                }
            } catch (error) {
                console.error("Erreur lors de la conversion en base64 : ", error);
            }
        }
    };

    return (
        <form>
            <div className="flex image-form">
                <fieldset>
                    <Icon
                        span={modalType === "portrait" && (pathname.includes("agence") ? "Logo de l'agence" : "Portrait du défunt")} source="fa fa-image" />
                    {/* Section Publication - affichée si modalType === "publication" ou "all" */}
                    {(pathname.includes("infos-defunt") || pathname?.includes("nouveau")) && (modalType === "publication" || modalType === "all") &&
                        <div className="form-input is-listed-input">
                            <label className="required is-listed-label" htmlFor="isListed">
                                Publier la page sur Internet ?
                            </label>
                            <div className="is-listed-options">
                                <div className="is-listed-option">
                                    <input type="radio" id="isListed1" name="isListed" value={1} required
                                        checked={values?.isListed === 1 || values?.isListed === "1"}
                                        onChange={onChange} />
                                    <label htmlFor="isListed1" className="is-listed-option-label">Oui</label>
                                </div>
                                <div className="is-listed-option">
                                    <input type="radio" id="isListed0" name="isListed" value={0}
                                        checked={values?.isListed === 0 || values?.isListed === "0"}
                                        onChange={onChange} />
                                    <label htmlFor="isListed0" className="is-listed-option-label">Non</label>
                                </div>
                            </div>
                        </div>
                    }

                    {/* Section Portrait - affichée si modalType === "portrait" ou "all" */}
                    {(modalType === "portrait" || modalType === "all") &&
                        <div className="deceased-portrait-section">
                            {/* Label du portrait */}
                            <label className="portrait-label-center">
                                {pathname.includes("agence") ? "Logo de l'agence" : "Portrait du défunt"}
                            </label>

                            {/* Aperçu du portrait - SEULEMENT l'image */}
                            <div className="portrait-preview-frame logo">{selectedImage ?
                                    <img src={selectedImage} alt="Image sélectionnée" /> :
                                    pathname.includes("agence") && logoFullUrl ?
                                        <img src={logoFullUrl} alt="Logo de l'agence" /> :
                                        deceasedInfos?.useGalleryPortrait && values?.portrait ?
                                            <img
                                                src={`${IMG_FOLDER}/img/deceased_library/${agencyId}/${values?.portrait}`}
                                                alt="Portrait de la bibliothèque"
                                                onError={(e) => {
                                                    e.target.onerror = null;
                                                    e.target.src = `${IMG_FOLDER}/img/deceased_library/${values?.portrait}`;
                                                }}
                                            /> :
                                            values?.portrait && deceasedInfos?.uploadDir ?
                                                <img src={`${IMG_FOLDER}/img/${deceasedInfos?.uploadDir}/${values?.portrait}`} alt="Portrait du défunt" /> :
                                                <span className="no-image-placeholder">Aucune image</span>
                                }
                            </div>

                            {/* Bouton unique pour modifier le portrait */}
                            <div className="portrait-button-container">
                                <button
                                    type="button"
                                    className="modify-portrait-button"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setShowPortraitModal(true);
                                    }}
                                >
                                    <i className="fa fa-image"></i>
                                    <span>{pathname.includes("agence") ? "Modifier le logo" : "Modifier le portrait"}</span>
                                </button>
                            </div>
                        </div>
                    }

                    {/* Modal Portrait Unifiée */}
                    {showPortraitModal && (pathname.includes("infos-defunt") || pathname?.includes("nouveau") || pathname.includes("agence")) && (
                        <div className="modal-overlay modal-overlay-secondary">
                            <div className="modal-content modal-portrait-unified" onClick={(e) => e.stopPropagation()}>
                                <div className="modal-header">
                                    <h3>
                                        <i className="fa fa-image"></i>
                                        {pathname.includes("agence") ? "Modifier le logo" : "Modifier le portrait"}
                                    </h3>
                                    <i className="fa fa-times close-button" onClick={() => setShowPortraitModal(false)}></i>
                                </div>
                                <div className="modal-body modal-body-portrait">
                                    {/* Section gauche: Bibliothèque - cachée pour les pages agence */}
                                    {!pathname.includes("agence") && (
                                    <div className="portrait-section portrait-library-section">
                                        <h4>
                                            <i className="fa fa-images"></i>
                                            Bibliothèque d'images
                                        </h4>
                                        <div className="library-images-container">
                                            <h5>Images de l'agence</h5>
                                            {/*<div className="library-images-grid">
                                                {agencyImageList?.map((image) => (
                                                    <div
                                                        key={image.uniqueKey}
                                                        className={`library-image-item ${selectedPortrait === image?.url ? "selected" : ""}`}
                                                        onClick={() => handleLibFileSelected(image?.url, image?.path)}
                                                    >
                                                        {selectedPortrait === image?.url && (
                                                            <i className="fa fa-check-circle" />
                                                        )}
                                                        <img
                                                            src={image.path}
                                                            alt="Portrait du défunt"
                                                            onError={(e) => {
                                                                e.target.style.display = 'none';
                                                                e.target.parentElement.style.display = 'none';
                                                            }}
                                                        />
                                                    </div>
                                                ))}
                                            </div>*/}

                                            <h5>Images communes (admin)</h5>
                                            <div className="library-images-grid">
                                                {rootImageList?.map((image) => (
                                                    <div
                                                        key={image.uniqueKey}
                                                        className={`library-image-item ${selectedPortrait === image?.url ? "selected" : ""}`}
                                                        onClick={() => handleLibFileSelected(image?.url, image?.path)}
                                                    >
                                                        {selectedPortrait === image?.url && (
                                                            <i className="fa fa-check-circle" />
                                                        )}
                                                        <img
                                                            src={image.path}
                                                            alt="Portrait du défunt"
                                                            onError={(e) => {
                                                                e.target.style.display = 'none';
                                                                e.target.parentElement.style.display = 'none';
                                                            }}
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                            {rootImageList?.length === 0 && (
                                                <p className="no-images">Aucune image disponible</p>
                                            )}
                                        </div>
                                    </div>
                                    )}

                                    {/* Section droite: Import */}
                                    <div className={`portrait-section portrait-upload-section ${pathname.includes("agence") ? "full-width" : ""}`}>
                                        <h4>
                                            <i className="fa fa-upload"></i>
                                            {pathname.includes("agence") ? "Importer un logo" : "Importer une image"}
                                        </h4>
                                        <div className="upload-content">
                                            {!selectedImage ? (
                                                <label htmlFor="portrait-other" className="upload-label">
                                                    <i className="fa fa-cloud-upload-alt"></i>
                                                    <span>Sélectionner une image</span>
                                                </label>
                                            ) : (
                                                <div className="upload-preview-compact">
                                                    <img src={selectedImage} alt="Aperçu" />
                                                    <div className="preview-info">
                                                        <i className="fa fa-check-circle"></i>
                                                        <p className="file-name">{fileName}</p>
                                                        <button
                                                            type="button"
                                                            className="change-image-btn"
                                                            onClick={() => {
                                                                setSelectedImage(null);
                                                                setSelectedCompressedFile(null);
                                                                setFileName("");
                                                            }}
                                                        >
                                                            <i className="fa fa-sync-alt"></i>
                                                            Changer
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                            <input
                                                type="file"
                                                name="portrait"
                                                accept="image/*"
                                                id="portrait-other"
                                                onChange={handleFileChange}
                                                className="hidden"
                                            />
                                        </div>
                                        <div className="portrait-upload-actions">
                                            <button type="button" className="red-button" onClick={() => setShowPortraitModal(false)}>
                                                <i className="fa fa-times"></i>
                                                <span>Annuler</span>
                                            </button>
                                            <button type="button" className="green-button" onClick={async (e) => {
                                                setShowPortraitModal(false);
                                                if (pathname.includes("agence")) {
                                                    await handleFileUpload(e);
                                                }
                                            }}>
                                                <i className="fa fa-check"></i>
                                                <span>Valider</span>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                </fieldset>
                {!pathname?.includes("nouveau") && <div className="errors"></div>}
            </div>
            {!pathname?.includes("nouveau") && (
                <div className="buttons-container">
                    <button className="green-button" onClick={handleFileUpload}>
                        <i className='fa fa-check'></i>
                        <span>Valider</span>
                    </button>
                    <button type="button" onClick={handleRemoveModal} className="red-button">
                        <i className="fa fa-xmark"></i>
                        <span>Annuler</span>
                    </button>
                </div>
            )}
        </form>
    )
}
